import { useEffect } from 'react'
import type {
  Country,
  Currency,
  PartialFormData,
  PlanInfo,
  PricingDatum,
  BillingCycle
} from '@/types/apply'
import {
  buildReadOnlyWebMcpTools,
  registerWebMcpTools,
  pickCurrency,
  asString,
  asStringArray,
  group,
  text,
  type WebMcpTool
} from '@/hooks/webMcpTools'

// WebMCP (Web Model Context Protocol) lets this page advertise structured,
// callable tools to an AI agent operating in the same browser context, via the
// W3C `navigator.modelContext` API (https://github.com/webmachinelearning/webmcp).
// As of 2026 it ships behind a Chrome origin trial, so EVERYTHING here is
// feature-detected: when the API is absent (the common case today) this hook is
// a no-op and the normal human form is untouched.
//
// The two read-only tools (list plans/pricing, check country) live in the
// shared `webMcpTools` module so the homepage can register them too; this hook
// adds the apply-only prefill tool on top.
//
// Design note (anti-abuse): the apply form is intentionally bot-protected
// (Turnstile), payment-gated (a small identity-verification charge), and
// human-reviewed. So the tools below READ public info and PREFILL the form, but
// they deliberately do NOT submit it or accept the legal agreements — the person
// must still verify their email OTP, pass the bot check, accept the terms, and
// complete the verification payment themselves.

export interface UseWebMcpArgs {
  plans: Record<string, PlanInfo[]>
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  countries: Country[]
  supportedCurrencies: Currency[]
  defaultCurrency: Currency
  mergeFormData: (patch: PartialFormData) => void
  goToStep: (step: number) => void
  onPrefill?: () => void
}

export function useWebMcp(args: UseWebMcpArgs) {
  const {
    plans,
    pricing,
    countries,
    supportedCurrencies,
    defaultCurrency,
    mergeFormData,
    goToStep,
    onPrefill
  } = args

  useEffect(() => {
    const pick = (c?: string): Currency => pickCurrency(supportedCurrencies, defaultCurrency, c)

    const readOnlyTools = buildReadOnlyWebMcpTools({
      plans,
      pricing,
      countries,
      supportedCurrencies,
      defaultCurrency
    })

    const prefillTool: WebMcpTool = {
      name: 'wavbee_prefill_application',
      description:
        "Pre-fill the wavbee application form on this page with the user's details. " +
        'IMPORTANT: this does NOT submit the application. After prefilling, the person must still ' +
        'verify their email with a one-time code, accept the terms themselves, pass the bot check, and ' +
        'pay the small one-time identity-verification fee. Every application is reviewed by a human. ' +
        'Only call this with details the user has explicitly provided.',
      inputSchema: {
        type: 'object',
        properties: {
          personal_info: {
            type: 'object',
            properties: {
              first_name: { type: 'string' },
              last_name: { type: 'string' },
              email: { type: 'string' },
              phone: { type: 'string' },
              website: { type: 'string' }
            }
          },
          address: {
            type: 'object',
            properties: {
              street_address: { type: 'string' },
              address_line_2: { type: 'string' },
              city: { type: 'string' },
              state: { type: 'string' },
              postal_code: { type: 'string' },
              country: { type: 'string' }
            }
          },
          role_info: {
            type: 'object',
            properties: {
              role: { type: 'string', enum: ['artist', 'label'] },
              artist_name: { type: 'string' },
              label_name: { type: 'string' }
            }
          },
          demo_links: {
            type: 'array',
            items: { type: 'string' },
            description: 'Links to the applicant\'s existing music (Spotify, YouTube, SoundCloud, etc.).'
          },
          social_media: {
            type: 'object',
            properties: {
              instagram: { type: 'string' },
              tiktok: { type: 'string' },
              twitter: { type: 'string' },
              facebook: { type: 'string' }
            }
          },
          subscription: {
            type: 'object',
            properties: {
              plan: { type: 'string', description: 'Plan key, e.g. artist, artist_pro, label, label_growth, label_pro.' },
              billing_cycle: { type: 'string', enum: ['monthly', 'yearly'] },
              currency: { type: 'string', enum: supportedCurrencies }
            }
          }
        }
      },
      execute: (a) => {
        const pi = group(a.personal_info)
        const addr = group(a.address)
        const role = group(a.role_info)
        const social = group(a.social_media)
        const sub = group(a.subscription)

        const roleVal = asString(role.role)
        const cycleVal = asString(sub.billing_cycle)

        const patch: PartialFormData = {
          personal_info: {
            ...(asString(pi.first_name) && { first_name: asString(pi.first_name) }),
            ...(asString(pi.last_name) && { last_name: asString(pi.last_name) }),
            ...(asString(pi.email) && { email: asString(pi.email) }),
            ...(asString(pi.phone) && { phone: asString(pi.phone) }),
            ...(asString(pi.website) && { website: asString(pi.website) })
          },
          address: {
            ...(asString(addr.street_address) && { street_address: asString(addr.street_address) }),
            ...(asString(addr.address_line_2) && { address_line_2: asString(addr.address_line_2) }),
            ...(asString(addr.city) && { city: asString(addr.city) }),
            ...(asString(addr.state) && { state: asString(addr.state) }),
            ...(asString(addr.postal_code) && { postal_code: asString(addr.postal_code) }),
            ...(asString(addr.country) && { country: asString(addr.country) })
          },
          role_info: {
            ...((roleVal === 'artist' || roleVal === 'label') && { role: roleVal }),
            ...(asString(role.artist_name) && { artist_name: asString(role.artist_name) }),
            ...(asString(role.label_name) && { label_name: asString(role.label_name) })
          },
          social_media: {
            ...(asString(social.instagram) && { instagram: asString(social.instagram) }),
            ...(asString(social.tiktok) && { tiktok: asString(social.tiktok) }),
            ...(asString(social.twitter) && { twitter: asString(social.twitter) }),
            ...(asString(social.facebook) && { facebook: asString(social.facebook) })
          },
          subscription: {
            ...(asString(sub.plan) && { plan: asString(sub.plan) }),
            ...((cycleVal === 'monthly' || cycleVal === 'yearly') && { billing_cycle: cycleVal }),
            ...(asString(sub.currency) && { currency: pick(asString(sub.currency)) })
          }
        }

        const demos = asStringArray(a.demo_links)
        if (demos) patch.demo_links = demos

        // Never let an agent accept the legal agreements on the user's behalf.
        delete patch.agreements

        mergeFormData(patch)
        goToStep(1)
        onPrefill?.()

        return text(
          'The application form has been pre-filled with the details provided. ' +
            'Nothing has been submitted yet. To finish, the user must: ' +
            '(1) review each step, (2) verify their email with the one-time code sent to them, ' +
            '(3) tick the terms and policy checkboxes themselves, (4) pass the on-page bot check, and ' +
            '(5) pay the small one-time identity-verification fee. A human then reviews the application ' +
            'within 1-3 business days.'
        )
      }
    }

    // provideContext replaces the whole toolset; a visitor is only ever on one
    // tool-registering surface at a time, so this is safe.
    return registerWebMcpTools([...readOnlyTools, prefillTool])
  }, [
    plans,
    pricing,
    countries,
    supportedCurrencies,
    defaultCurrency,
    mergeFormData,
    goToStep,
    onPrefill
  ])
}
